import { useState } from 'react'
import { Button, ErrorBanner, Field, Modal, Select, TextInput } from '../../components/forms'
import { useApprovers, type ApprovalNeed } from '../../features/pos/api'
import { SendApprovalCode } from './SendApprovalCode'

export type ApprovalAnswer = { reason: string } | { reason: string; approverUserId: string; pin: string }

/**
 * Who approves this, and why.
 *
 * When the seller holds the authority themselves the dialogue asks only for a
 * reason. Otherwise the approver is **texted a one-time code** and the cashier types what the
 * approver reads back (D-240) — the server checks that approver's own role and limit, never the
 * cashier's (D-048). The code box is cleared on every attempt and never kept.
 *
 * Why a text and not the PIN this used to ask for: a PIN has to be set in advance, and at the time
 * this was built not one person in the company had set one, so every approval the till asked for was
 * impossible to give. A code also reaches a manager who is not in the building, which the PIN never
 * could. Somebody who *has* set a PIN can still use it — the same box takes either — because SMS is
 * not a guarantee and a till that cannot finish a sale is the worse failure.
 */
export function ApprovalDialog({
  locationId,
  needs,
  saleValue,
  sellerCanApprove,
  busy,
  error,
  onApprove,
  onCancel,
}: {
  locationId: string
  needs: ApprovalNeed[]
  /** What the customer would pay, so the approver's message names the sale they are agreeing to. */
  saleValue?: number
  sellerCanApprove: boolean
  busy: boolean
  error: unknown
  onApprove: (answer: ApprovalAnswer) => void
  onCancel: () => void
}) {
  const approvers = useApprovers(locationId, !sellerCanApprove)
  const [approverId, setApproverId] = useState('')
  const [pin, setPin] = useState('')
  const [reason, setReason] = useState('')

  // Only people whose authority covers every need are offered. A 10% supervisor is
  // not listed for a 12% discount: choosing them would only produce a refusal.
  const discountNeeded = Math.max(0, ...needs.filter((n) => n.kind === 'Discount').map((n) => n.discountPercent ?? 0))
  const eligible = (approvers.data ?? []).filter(
    (a) =>
      (!needs.some((n) => n.kind === 'Discount') || (a.canApproveDiscount && (a.discountLimit === null || a.discountLimit === undefined || a.discountLimit >= discountNeeded))) &&
      (!needs.some((n) => n.kind === 'Price') || a.canOverridePrice) &&
      (!needs.some((n) => n.kind === 'Credit') || a.canOverrideCredit),
  )

  const ready = reason.trim().length > 0 && (sellerCanApprove || (approverId && pin.length >= 4))
  const chosen = eligible.find((a) => a.id === approverId)

  function submit() {
    if (!ready) return
    onApprove(sellerCanApprove ? { reason: reason.trim() } : { reason: reason.trim(), approverUserId: approverId, pin })
    setPin('')
  }


  return (
    <Modal
      open
      title={sellerCanApprove ? 'Approve this sale' : 'Manager approval needed'}
      onClose={onCancel}
      footer={
        <>
          <Button tone="secondary" onClick={onCancel}>
            Back to the sale
          </Button>
          <Button busy={busy} disabled={!ready} onClick={submit}>
            Approve and charge
          </Button>
        </>
      }
    >
      <ul className="mb-4 space-y-1.5 text-sm">
        {needs.map((n) => (
          <li key={n.reason} className="rounded-md bg-dpl-accent/10 px-3 py-2 text-[#6B4700]">
            {n.reason}
          </li>
        ))}
      </ul>

      <form
        className="grid gap-4"
        onSubmit={(e) => {
          e.preventDefault()
          submit()
        }}
      >
        {!sellerCanApprove && (
          <>
            <Field label="Approved by" htmlFor="approver" hint={eligible.length === 0 && !approvers.isPending ? 'Nobody at this shop can approve all of this. Change the sale, or call the owner.' : undefined}>
              <Select
                id="approver"
                value={approverId}
                onChange={(e) => {
                  setApproverId(e.target.value)
                  setPin('')
                }}
              >
                <option value="">Choose…</option>
                {eligible.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name} — {a.role}
                  </option>
                ))}
              </Select>
            </Field>

            <SendApprovalCode
              locationId={locationId}
              approver={chosen}
              purpose={
                needs.some((n) => n.kind === 'Discount')
                  ? 'Discount'
                  : needs.some((n) => n.kind === 'Credit')
                    ? 'CreditOverride'
                    : 'PriceOverride'
              }
              // The DEEPEST discount on the basket is what the approver is asked about. Sending the
              // shallowest would understate what they are agreeing to, and the server re-checks its
              // own figures when the code is spent, so it would only produce a refusal.
              discountPercent={discountNeeded > 0 ? discountNeeded : undefined}
              saleValue={saleValue}
            />

            <Field
              label="Their code"
              htmlFor="approver-pin"
              hint="The six digits from their phone — or their PIN, if they have set one."
            >
              <TextInput
                id="approver-pin"
                type="password"
                inputMode="numeric"
                autoComplete="off"
                maxLength={8}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))}
              />
            </Field>
          </>
        )}
        <Field label="Reason" htmlFor="approval-reason" hint="Recorded with the sale and audited.">
          <TextInput id="approval-reason" value={reason} onChange={(e) => setReason(e.target.value)} autoFocus={sellerCanApprove} />
        </Field>
        <ErrorBanner error={error} />
        <button type="submit" className="hidden" />
      </form>
    </Modal>
  )
}
